<template>
  <v-dialog v-model="showDialog" max-width="460" persistent>
    <v-card class="edit-item-dialog">
      <div class="d-flex align-center justify-space-between px-4 py-3 dialog-header">
        <div class="d-flex align-center">
          <v-icon size="20" color="#c2410c" class="mr-2">mdi-pencil-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Edit Container Details</span>
        </div>
        <v-btn icon small @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="px-4 py-3 container-strip">
        <span class="overline grey--text mr-2">Container</span>
        <span class="container-chip mono">{{ item ? item.container_number : "" }}</span>
      </div>
      <v-divider />

      <v-card-text class="pa-4" :key="`${item ? item.id : 'none'}-${value}`">
        <div v-if="remarkLines.length" class="issue-card-list mb-4">
          <div v-for="(line, lineIndex) in remarkLines" :key="lineIndex" class="issue-card">
            <v-icon x-small color="#92400e" class="mr-2 mt-1">mdi-alert-circle-outline</v-icon>
            <span>{{ line }}</span>
          </div>
        </div>

        <div class="field-label">Container Type *</div>
        <MultiSelect
          hide-details="auto"
          outlined
          dense
          class="mb-5"
          :items="containerTypeOptions"
          itemText="type_name"
          itemValue="id"
          :returnObject="false"
          v-model="form.container_type"
        />

        <div class="field-label">Movement Date</div>
        <DateField
          v-model="form.movement_date"
          class="mb-5"
          :inputProps="{ hideDetails: 'auto', outlined: true, dense: true }"
        />

        <div class="field-label">Route</div>
        <div class="route-row">
          <div class="route-field">
            <div class="route-field__eyebrow">Origin *</div>
            <MultiSelect
              hide-details="auto"
              outlined
              dense
              :items="locationOptions"
              itemText="location_code"
              itemValue="id"
              :returnObject="false"
              v-model="form.origin"
            />
          </div>
          <v-icon size="18" color="#9ca3af" class="route-arrow">mdi-arrow-right</v-icon>
          <div class="route-field">
            <div class="route-field__eyebrow">Destination *</div>
            <MultiSelect
              hide-details="auto"
              outlined
              dense
              :items="locationOptions"
              itemText="location_code"
              itemValue="id"
              :returnObject="false"
              v-model="form.destination"
            />
          </div>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text color="grey darken-1" class="action-btn mr-2" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          elevation="2"
          class="action-btn"
          :loading="saving"
          :disabled="!form.container_type || !form.origin || !form.destination"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";

export default {
  components: {
    MultiSelect,
    DateField,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    role: {
      type: String,
      default: "admin",
      validator: (value) => ["admin", "vendor"].includes(value),
    },
  },
  data() {
    return {
      form: {
        container_type: null,
        movement_date: null,
        origin: null,
        destination: null,
      },
      containerTypeOptions: [],
      locationOptions: [],
      saving: false,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    remarkLines() {
      if (!this.item || !this.item.remarks) return [];
      return this.item.remarks.split("\n").filter((line) => line);
    },
  },
  watch: {
    value(newValue) {
      if (newValue) {
        this.resetForm();
        this.fetchOptions();
      }
    },
  },
  methods: {
    resetForm() {
      this.form = {
        container_type: this.item ? this.item.container_type : null,
        movement_date: this.item ? this.item.movement_date : null,
        origin: this.item ? this.item.origin : null,
        destination: this.item ? this.item.destination : null,
      };
    },
    fetchOptions() {
      this.$api.containerType
        .getContainerTypeSelectList({ limit: "all" })
        .then((res) => {
          this.containerTypeOptions = res.data;
        })
        .catch(() => {});
      this.$api.location
        .getLocationSelectList({ limit: "all" })
        .then((res) => {
          this.locationOptions = res.data;
        })
        .catch(() => {});
    },
    close() {
      this.showDialog = false;
    },
    save() {
      if (!this.item) return;
      this.saving = true;
      const payload = {
        container_type: this.form.container_type,
        movement_date: this.form.movement_date,
        origin: this.form.origin,
        destination: this.form.destination,
      };
      const request =
        this.role === "vendor"
          ? this.$api.vendorSubHireUpload.editVendorDraftJobItem(this.item.id, payload)
          : this.$api.vendorSubHireUpload.editDraftJobItem(this.item.id, payload);
      request
        .then(() => {
          this.saving = false;
          this.$bus.$emit("showToastMessage", {
            message: "Container details updated.",
            color: "success",
          });
          this.$emit("saved");
          this.close();
        })
        .catch((err) => {
          this.saving = false;
          const message =
            (err && err.data && err.data.detail) ||
            (err && err.message) ||
            "Failed to update container details.";
          this.$bus.$emit("showToastMessage", { message, color: "error" });
        });
    },
  },
};
</script>

<style scoped>
.edit-item-dialog {
  border-radius: 10px !important;
  overflow: hidden;
}

.dialog-header {
  background-color: #fff7ed;
}

.container-strip {
  background-color: #fafafa;
  display: flex;
  align-items: center;
}

.issue-card-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.issue-card {
  display: flex;
  align-items: flex-start;
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.4;
}

.container-chip {
  background-color: #eef2ff;
  color: #3730a3;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 0.85rem;
  font-weight: 700;
}

.mono {
  font-family: inherit;
}

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #757575;
  margin-bottom: 6px;
}

.route-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.route-field {
  flex: 1 1 0;
  min-width: 0;
}
.route-field__eyebrow {
  font-size: 10.5px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 4px;
}
.route-arrow {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
}
</style>
